import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import useMediaQuery from 'hooks/useMediaQuery';

import { getFromLocalStorage } from 'lib/localStorage';
import { parseToBool } from 'lib/string';

import LOCAL_STORAGE from 'constants/localStorage';

const AD_CLIENT = 'ca-pub-9700650747779397';

const AD_SLOTS = {
  SIDEBAR_300x250: '8833388776',
  LANDSCAPE_320x50: '9875007702',
  LANDSCAPE_728x90: '5229185683',
};

const isValidBreakpointValue = (value) => value !== null && value !== undefined;

/**
 * Resolves whether an ad slot should be active based on the current
 * viewport breakpoint and the configured visibility array.
 *
 * @param {boolean[]} breakpoints - Current breakpoint state [isSm, isMd, isLg, isXl]
 * @param {Array} showOnBreakpoints - Visibility config per breakpoint (true/false/null)
 * @returns {boolean}
 */
const resolveBreakpointActive = (breakpoints, showOnBreakpoints) => {
  if (showOnBreakpoints.length === 0) return true;

  const currBreakpointIndex = breakpoints.lastIndexOf(true);

  // SSR or first render before useMediaQuery resolves
  if (currBreakpointIndex === -1) return false;

  if (showOnBreakpoints.length === breakpoints.length) {
    const currBreakpointValue = showOnBreakpoints[currBreakpointIndex];
    if (currBreakpointValue !== null) return currBreakpointValue;
  }

  for (let index = showOnBreakpoints.length - 1; index >= 0; index--) {
    if (index > currBreakpointIndex) continue;
    if (index === 0) return showOnBreakpoints[0] ?? true;
    if (isValidBreakpointValue(showOnBreakpoints[index])) {
      return showOnBreakpoints[index];
    }
  }

  return true;
};

const AdSenseSlot = ({
  slotId,
  width,
  height,
  className,
  showAdSlotOnBreakpoints = [],
  ...rest
}) => {
  const adRef = useRef(null);
  const pushedRef = useRef(false);
  const { isSm, isMd, isLg, isXl } = useMediaQuery();

  const isActive = resolveBreakpointActive(
    [isSm, isMd, isLg, isXl],
    showAdSlotOnBreakpoints
  );

  useEffect(() => {
    if (!isActive || pushedRef.current || !adRef.current) return;

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
    } catch (error) {
      import('@sentry/nextjs').then(({ captureException }) => {
        captureException(error);
      });
    }
  }, [isActive]);

  if (!isActive) return null;

  const isPersonalized = parseToBool(
    getFromLocalStorage(LOCAL_STORAGE.IS_POLICY_NUMBER_3_ACCEPTED)
  );

  return (
    <ins
      ref={adRef}
      className={twMerge('adsbygoogle', className)}
      data-ad-client={AD_CLIENT}
      data-ad-format={width ? undefined : 'horizontal'}
      data-ad-personalization={isPersonalized ? 'on' : 'off'}
      data-ad-slot={slotId}
      style={{ display: 'block', width: width || '100%', height }}
      {...rest}
    />
  );
};

AdSenseSlot.propTypes = {
  slotId: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  className: PropTypes.string,
  showAdSlotOnBreakpoints: PropTypes.array,
};

export { AD_SLOTS, resolveBreakpointActive };
export default AdSenseSlot;
